import { installAuthFetchInterceptor } from './network/authFetch.js';
import { GameScene } from './game/GameScene.js';
import { ensurePhaserReadyForPlay, getPhaserGame } from './game/phaserApp.js';
import { syncPlayCanvasToContainer } from './game/phaserPlayCanvas.js';
import { mountDivisionsApp } from './divisions/DivisionsApp.js';
import { mountDashboardApp } from './dashboard/DashboardApp.js';
import { mountAccueilApp } from './dashboard/AccueilApp.js';
import { mountGarageApp } from './garage/GarageApp.js';
import { redirectLegacyGarageHash } from './garage/garageHash.js';
import { mountSeasonsApp, refreshSeasonsApp } from './seasons/SeasonsApp.js';
import { mountSettingsApp } from './settings/SettingsApp.js';
import { mountArchivesApp } from './raceHistory/ArchivesApp.js';
import { initThemeToggleShortcut, readGameCanvasColor } from './ui/theme.js';
import { initServerWatchdog } from './network/serverWatchdog.js';
import { teardownArchiveReplay } from './raceHistory/ArchivesApp.js';
import { mountAuthApp } from './auth/AuthApp.js';
import { mountAccountsApp } from './auth/AccountsApp.js';
import { mountBoardSettingsApp } from './board/BoardSettingsApp.js';
import { mountBoardView, teardownBoardView } from './board/mountBoardView.js';
import { renderNavAccount, applyRoleVisibility } from './auth/navAccount.js';
import { getCurrentUser, refreshSessionUser } from './auth/session.js';

installAuthFetchInterceptor();

const phaserGame = getPhaserGame();

function syncPhaserBackground(): void {
  const hex = readGameCanvasColor();
  (phaserGame.config as unknown as { backgroundColor: string | number }).backgroundColor = hex;
  const scene = phaserGame.scene.getScene('GameScene');
  if (scene?.cameras?.main) {
    scene.cameras.main.setBackgroundColor(hex);
  }
}

window.addEventListener('multirx-theme-change', syncPhaserBackground);
let playLayoutRefreshTimer: ReturnType<typeof setTimeout> | null = null;
window.addEventListener('multirx-play-layout-change', () => {
  if (playLayoutRefreshTimer) clearTimeout(playLayoutRefreshTimer);
  playLayoutRefreshTimer = setTimeout(() => {
    playLayoutRefreshTimer = null;
    if (!document.getElementById('view-course')?.classList.contains('is-active')) return;
    syncPlayCanvasToContainer(phaserGame);
    phaserGame.scale.refresh();
    const scene = phaserGame.scene.getScene('GameScene') as GameScene;
    scene?.handlePlayAreaResize?.();
  }, 80);
});
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if (!document.documentElement.getAttribute('data-theme')) syncPhaserBackground();
});

type ViewName =
  | 'accueil'
  | 'dashboard'
  | 'course'
  | 'garage'
  | 'saisons'
  | 'circuits'
  | 'divisions'
  | 'voitures'
  | 'pilotes'
  | 'assets'
  | 'archives'
  | 'parametres'
  | 'connexion'
  | 'comptes'
  | 'plateau-reglages'
  | 'plateau';

const viewEls: Record<ViewName, HTMLElement | null> = {
  accueil: document.getElementById('view-accueil'),
  dashboard: document.getElementById('view-dashboard'),
  course: document.getElementById('view-course'),
  garage: document.getElementById('view-garage'),
  saisons: document.getElementById('view-saisons'),
  circuits: document.getElementById('view-circuits'),
  divisions: document.getElementById('view-divisions'),
  voitures: document.getElementById('view-voitures'),
  pilotes: document.getElementById('view-pilotes'),
  assets: document.getElementById('view-assets'),
  archives: document.getElementById('view-archives'),
  parametres: document.getElementById('view-parametres'),
  connexion: document.getElementById('view-connexion'),
  comptes: document.getElementById('view-comptes'),
  'plateau-reglages': document.getElementById('view-plateau-reglages'),
  plateau: document.getElementById('view-plateau'),
};

const navEls: Partial<Record<ViewName | 'garage' | 'saisons', HTMLElement | null>> = {
  garage: document.getElementById('nav-garage'),
  connexion: document.getElementById('nav-paddock'),
  saisons: document.getElementById('nav-saisons'),
  archives: document.getElementById('nav-archives'),
  dashboard: document.getElementById('nav-dashboard'),
};

const mounted: Partial<Record<ViewName, boolean>> = {};

const allViews = Object.values(viewEls).filter(Boolean) as HTMLElement[];
const allNavs = Object.values(navEls).filter(Boolean) as HTMLElement[];

function setActiveView(activeView: HTMLElement | null, activeNav: HTMLElement | null): void {
  allViews.forEach((v) => v.classList.remove('is-active'));
  allNavs.forEach((n) => n.classList.remove('active'));
  activeView?.classList.add('is-active');
  activeNav?.classList.add('active');
}

function mountOnce(name: ViewName, mount: (el: HTMLElement) => void): void {
  const el = viewEls[name];
  if (!el || mounted[name]) return;
  mount(el);
  mounted[name] = true;
}

function safeTeardownArchiveReplay(): void {
  try {
    teardownArchiveReplay();
  } catch (err) {
    console.error('[MultiRX] Impossible de libérer le replay archives :', err);
  }
}

function showView(name: ViewName): void {
  if (name !== 'plateau') teardownBoardView();
  const navMap: Partial<Record<ViewName, HTMLElement | null>> = {
    garage: navEls.garage ?? null,
    circuits: navEls.garage ?? null,
    voitures: navEls.garage ?? null,
    pilotes: navEls.garage ?? null,
    assets: navEls.garage ?? null,
    saisons: navEls.saisons ?? null,
    divisions: navEls.saisons ?? null,
    archives: navEls.archives ?? null,
    dashboard: navEls.dashboard ?? null,
    parametres: navEls.dashboard ?? null,
  };

  if (name === 'archives') {
    const el = viewEls.archives;
    if (el) mountArchivesApp(el);
    setActiveView(viewEls.archives, navMap.archives ?? null);
    return;
  }

  if (name === 'connexion') {
    const el = viewEls.connexion;
    if (el) mountAuthApp(el);
    setActiveView(viewEls.connexion, navEls.connexion ?? null);
    return;
  }

  if (name === 'comptes') {
    const el = viewEls.comptes;
    if (el) mountAccountsApp(el);
    setActiveView(viewEls.comptes, null);
    return;
  }

  if (name === 'plateau-reglages') {
    const el = viewEls['plateau-reglages'];
    if (el) mountBoardSettingsApp(el);
    setActiveView(viewEls['plateau-reglages'], null);
    return;
  }

  if (name === 'plateau') {
    const el = viewEls.plateau;
    if (el) mountBoardView(el);
    setActiveView(viewEls.plateau, null);
    return;
  }

  if (name === 'course') {
    setActiveView(viewEls.course, navMap.course ?? null);
    safeTeardownArchiveReplay();
    ensurePhaserReadyForPlay();
    const scene = phaserGame.scene.getScene('GameScene') as GameScene;
    scene?.resetToPreRaceIfFinished?.();
    requestAnimationFrame(() => {
      ensurePhaserReadyForPlay();
      syncPlayCanvasToContainer(phaserGame);
      phaserGame.scale.refresh();
      scene?.handlePlayAreaResize?.();
      scene?.diceUI?.reloadCircuits()?.catch(() => undefined);
      void scene?.diceUI?.tryBootstrapTestRaceFromHash?.();
      void scene?.diceUI?.tryBootstrapMeetingFromHash?.();
      requestAnimationFrame(() => {
        syncPlayCanvasToContainer(phaserGame);
        scene?.handlePlayAreaResize?.();
      });
    });
    return;
  }

  if (name === 'accueil') mountOnce('accueil', mountAccueilApp);
  else if (name === 'dashboard') mountOnce('dashboard', mountDashboardApp);
  else if (name === 'garage') {
    // Remonter / resync à chaque navigation `#garage/...` (pas mountOnce).
    const el = viewEls.garage;
    if (el) mountGarageApp(el);
    mounted.garage = true;
  } else if (name === 'saisons') {
    mountOnce('saisons', mountSeasonsApp);
    refreshSeasonsApp();
  } else if (name === 'divisions') mountOnce('divisions', mountDivisionsApp);
  else if (name === 'parametres') mountOnce('parametres', mountSettingsApp);

  setActiveView(viewEls[name], navMap[name] ?? null);
  safeTeardownArchiveReplay();
}

/** Segment de vue sans query (?meetingSession=…) ni sous-chemin (/id). */
function hashViewSegment(): string {
  const raw = window.location.hash.replace(/^#/, '').trim();
  if (!raw) return 'accueil';
  const path = (raw.split('?')[0] ?? '').replace(/^\/+/, '');
  const segment = path.split('/').filter(Boolean)[0];
  return segment || 'accueil';
}

function parseHash(): ViewName {
  const view = hashViewSegment();
  if (view === 'jeu') return 'course';
  if (view === 'accueil' || view === 'home') return 'accueil';
  if (view === 'dashboard') return 'dashboard';
  if (view === 'course') return 'course';
  if (view === 'garage') return 'garage';
  if (view === 'ligue') return 'saisons';
  if (view === 'saisons') return 'saisons';
  // Anciennes URLs → vue garage (redirectLegacyGarageHash normalise le hash).
  if (view === 'circuits' || view === 'voitures' || view === 'pilotes' || view === 'assets') {
    return 'garage';
  }
  if (view === 'divisions') return 'divisions';
  // Ancienne page autonome : redirige vers Saisons, qui embarque le même classement.
  if (view === 'classement') return 'saisons';
  if (view === 'archives') return 'archives';
  if (view === 'parametres') return 'parametres';
  if (view === 'connexion') return 'connexion';
  if (view === 'comptes') return 'comptes';
  if (view === 'plateau-reglages') return 'plateau-reglages';
  if (view === 'plateau') return 'plateau';
  return 'accueil';
}

function ensureDefaultHash(): void {
  const raw = window.location.hash.replace(/^#/, '').trim();
  if (!raw) {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#accueil`);
  }
}

/** Tout sauf la page de connexion exige un compte : sinon, on y renvoie (page d'accueil vide). */
function onHashChange(): void {
  if (window.location.hash === '#jeu') {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#course`);
  }
  const legacy = redirectLegacyGarageHash(window.location.hash);
  if (legacy) {
    history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#${legacy}`
    );
  }
  const target = parseHash();
  if (target !== 'connexion' && !getCurrentUser()) {
    if (window.location.hash !== '#connexion') {
      window.location.hash = 'connexion';
      return;
    }
  }
  showView(target);
  closeNavDrawer();
}

function closeNavDrawer(): void {
  const drawer = document.getElementById('app-nav-drawer');
  const btn = document.getElementById('app-nav-menu-btn');
  drawer?.setAttribute('hidden', '');
  btn?.setAttribute('aria-expanded', 'false');
}

function openNavDrawer(): void {
  const drawer = document.getElementById('app-nav-drawer');
  const btn = document.getElementById('app-nav-menu-btn');
  drawer?.removeAttribute('hidden');
  btn?.setAttribute('aria-expanded', 'true');
}

document.getElementById('app-nav-menu-btn')?.addEventListener('click', () => {
  const drawer = document.getElementById('app-nav-drawer');
  if (drawer?.hasAttribute('hidden')) openNavDrawer();
  else closeNavDrawer();
});

document.getElementById('app-nav-drawer')?.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (t.tagName === 'A') closeNavDrawer();
});

document.getElementById('app-nav-brand')?.addEventListener('click', (e) => {
  e.preventDefault();
  window.location.hash = 'accueil';
});

document.querySelectorAll('#app-nav-links a, #app-nav-drawer a').forEach((link) => {
  link.addEventListener('click', (e) => {
    e.preventDefault();
    const href = (link as HTMLAnchorElement).getAttribute('href');
    if (href) window.location.hash = href.replace(/^#/, '');
  });
});

initThemeToggleShortcut();
initServerWatchdog();
ensureDefaultHash();
window.addEventListener('hashchange', onHashChange);

void (async (): Promise<void> => {
  await refreshSessionUser();
  renderNavAccount();
  applyRoleVisibility();
  onHashChange();
  syncPhaserBackground();
})();
